<script lang="ts">
import { Prop } from '@/helpers/Decorators';
import { KeyValuePair } from '@/helpers/Interfaces';
import { DynamicDocument } from '@/modules/low-code/services/ModulesService';
import { Options, Vue } from 'vue-class-component';
import { axios } from '@/plugins/axios';
import { scrollRedirect } from '@/components/common/dynamic-grid/helpers/scrollRedirect';

// Updated interface for the new approach
interface SimpleDynamicDocument {
    key: string;
    title: string;
}

// Response interface for permission check
interface PermissionCheckResponse {
    canSee: boolean;
    baseUrl: string;
}

@Options({
    name: 'DynamicDocumentFormatter',
})
export default class DynamicDocumentFormatter extends Vue
{
    @Prop({ default: null }) public property: SimpleDynamicDocument | SimpleDynamicDocument[] | null;
    @Prop({ default: (): KeyValuePair[] => [] }) public features: KeyValuePair[];
    @Prop({ default: false }) public isOnGrid: boolean;

    private permissionCache = new Map<string, PermissionCheckResponse>();

    public async checkDocumentPermission(publicId: string): Promise<PermissionCheckResponse>
    {
        // Check cache first
        if (this.permissionCache.has(publicId))
        {
            return this.permissionCache.get(publicId) as PermissionCheckResponse;
        }

        try
        {
            const response = await axios.get<PermissionCheckResponse>(`documents/${publicId}/can-see`);

            this.permissionCache.set(publicId, response.data);

            return response.data;
        }
        catch (error)
        {
            // If API call fails, assume no permission
            const noPermission: PermissionCheckResponse = { canSee: false, baseUrl: '' };

            this.permissionCache.set(publicId, noPermission);

            return noPermission;
        }
    }

    public async handleDocumentClick(event: Event, doc: SimpleDynamicDocument): Promise<void>
    {
        event.preventDefault();

        const permission = await this.checkDocumentPermission(doc.key);

        if (permission.canSee)
        {
            this.$router.push({
                name: 'module-form-details',
                params: {
                    publicId: doc.key,
                    licence: permission.baseUrl
                }
            });
        }
        else
        {
            this.$alert.warning('Nie posiadzasz uprawnień do tego dokumentu');
        }
    }

    public async handleScrollClick(event: Event, doc: SimpleDynamicDocument): Promise<void>
    {
        event.preventDefault();

        const permission = await this.checkDocumentPermission(doc.key);

        if (permission.canSee)
        {
            scrollRedirect({
                name: 'module-form-details',
                params: { licence: permission.baseUrl, publicId: doc.key },
            });
        }
        else
        {
            this.$alert.warning('Nie posiadzasz uprawnień do tego dokumentu');
        }
    }

    public hasNoPermission(publicId: string): boolean
    {
        const permission = this.permissionCache.get(publicId);

        return permission && !permission.canSee;
    }
}
</script>

<template>
    <div v-if="Array.isArray(property)">
        <div v-for="doc in property" :key="doc.key">
            <div
                href="#"
                @click="handleDocumentClick($event, doc)"
                @click.middle="handleScrollClick($event, doc)"
                :class="['document-link', { 'no-permission': hasNoPermission(doc.key) }]"
            >
                {{ doc.title }}
            </div>
        </div>
    </div>
    <div v-else-if="property">
        <div
            href="#"
            @click="handleDocumentClick($event, property)"
            @click.middle="handleScrollClick($event, property)"
            :class="['document-link', { 'no-permission': hasNoPermission(property.key) }]"
        >
            {{ property.title }}
        </div>
    </div>
</template>

<style scoped>
.document-link {
    color: #ff9800;
    text-decoration: none;
    cursor: pointer;
}

.document-link:hover {
    text-decoration: underline;
}

.document-link.no-permission {
    color: #6c757d;
    cursor: not-allowed;
}

.document-link.no-permission:hover {
    text-decoration: none;
}
</style>

